import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/system/Box";
import React from "react";
import ImageBox from "./ImageBox";
import Link from "@shared/components/contents/link/Link";
const scrollAnimationStyles = {
    "@keyframes scroll": {
        "0%": { transform: "translateX(0)" },
        "100%": { transform: "translateX(-25%)" },
    },
};
const scrollSpeed = 30;
const trackSx = {
    display: "flex",
    flexShrink: 0,
    willChange: "transform",
    animation: "scroll 60s linear infinite",
};
const groupSx = {
    display: "flex",
    flexShrink: 0,
};
const AutoScrollingInfos = (props) => {
    const { id, nbRow = "2", dynamicList = [], ...others } = props;
    const [activeLinkItemId, setActiveLinkItemId] = React.useState(null);
    const [groupRepeatCounts, setGroupRepeatCounts] = React.useState([]);
    const [scrollTrackDurations, setScrollTrackDurations] = React.useState([]);
    const scrollingInfosRef = React.useRef(null);
    const groupRefs = React.useRef([]);
    const trackRefs = React.useRef([]);
    const nbRowNumber = parseInt(nbRow && nbRow !== "" ? nbRow : "2", 10);
    const isUniqueRow = nbRowNumber <= 1;
    const rowItems = React.useMemo(() => dynamicList.reduce((rows, item, index) => {
        const nbItemsPerRow = Math.ceil((dynamicList.length ?? 0) / nbRowNumber);
        if (index % nbItemsPerRow === 0) {
            rows.push([]);
        }
        rows[rows.length - 1]?.push(item);
        return rows;
    }, []), [dynamicList, nbRowNumber]);
    React.useEffect(() => {
        const container = scrollingInfosRef.current;
        if (!container) {
            return;
        }
        const updateGroupRepeatCounts = () => {
            const containerWidth = container.getBoundingClientRect().width;
            const nextCounts = groupRefs.current.map((group) => {
                const groupWidth = group?.getBoundingClientRect().width ?? 0;
                return groupWidth > 0 && containerWidth > 0
                    ? Math.max(2, Math.ceil(containerWidth / groupWidth) + 1)
                    : 2;
            });
            setGroupRepeatCounts((currentCounts) => currentCounts.length === nextCounts.length &&
                currentCounts.every((count, index) => count === nextCounts[index])
                ? currentCounts
                : nextCounts);
            const nextDurations = trackRefs.current.map((track) => {
                const trackWidth = track?.getBoundingClientRect().width ?? 0;
                const currentScrollSpeed = containerWidth <= 600 ? scrollSpeed * 1.2 : scrollSpeed;
                return trackWidth > 0 ? (trackWidth * 0.25) / currentScrollSpeed : 40;
            });
            setScrollTrackDurations((currentDurations) => currentDurations.length === nextDurations.length &&
                currentDurations.every((duration, index) => duration === nextDurations[index])
                ? currentDurations
                : nextDurations);
        };
        updateGroupRepeatCounts();
        const resizeObserver = new ResizeObserver(updateGroupRepeatCounts);
        resizeObserver.observe(container);
        groupRefs.current.forEach((group) => {
            if (group) {
                resizeObserver.observe(group);
            }
        });
        trackRefs.current.forEach((track) => {
            if (track) {
                resizeObserver.observe(track);
            }
        });
        return () => resizeObserver.disconnect();
    }, [dynamicList, nbRowNumber, rowItems.length, groupRepeatCounts]);
    if (nbRowNumber <= 0 || dynamicList.length === 0) {
        return null;
    }
    return (_jsx(Stack, { ref: scrollingInfosRef, spacing: 3, ...others, sx: {
            ...scrollAnimationStyles,
            "@media (hover: hover)": {
                "&:hover .scroll-track, &:focus-within .scroll-track": {
                    animationPlayState: "paused",
                },
            },
        }, children: rowItems.map((row, rowIndex) => {
            const isEvenRow = rowIndex % 2 === 0;
            const reverseDirection = nbRowNumber > 1 && !isEvenRow;
            const groupContent = row.map((item, index) => {
                const itemId = `${rowIndex}-${index}`;
                const hasLink = !!(item.link?.page || item.link?.url);
                const isDimmed = activeLinkItemId !== null && activeLinkItemId !== itemId;
                return (_jsxs(Stack, { direction: "row", spacing: 1.5, title: item.title, component: hasLink ? Link : "div", ...(hasLink ? item.link : {}), onMouseEnter: () => setActiveLinkItemId(itemId), onMouseLeave: () => setActiveLinkItemId(null), onFocus: () => setActiveLinkItemId(itemId), onBlur: () => setActiveLinkItemId(null), sx: {
                        padding: isUniqueRow ? 4 : 2,
                        alignItems: "center",
                        border: "1px solid #FFFFFF26",
                        backgroundColor: "#FFFFFF26",
                        flexShrink: 0,
                        opacity: isDimmed ? 0.35 : 1,
                        filter: isDimmed ? "grayscale(1)" : "none",
                        transition: "opacity 0.25s ease, filter 0.25s ease",
                        ...(reverseDirection && { transform: "scaleX(-1)" }),
                    }, children: [item.image && (_jsx(ImageBox, { image: item.image, isUniqueRow: isUniqueRow })), item.title && !isUniqueRow && (_jsx(Typography, { variant: "body1", sx: { color: "primary.contrastText" }, children: item.title }))] }, item.title + index));
            });
            return (_jsx(StyledBox, { isUniqueRow: isUniqueRow, reverse: reverseDirection, children: _jsx(Stack, { className: "scroll-track", direction: "row", ref: (element) => {
                        trackRefs.current[rowIndex] = element;
                    }, sx: {
                        ...trackSx,
                        animationDuration: `${scrollTrackDurations[rowIndex] ?? 60}s`,
                    }, children: Array.from({ length: 2 }, (_, copySetIndex) => (_jsx(Stack, { direction: "row", spacing: 3, sx: { flexShrink: 0 }, children: Array.from({ length: groupRepeatCounts[rowIndex] ?? 2 }, (_, groupIndex) => (_jsx(Stack, { ref: copySetIndex === 0 && groupIndex === 0
                                ? (element) => {
                                    groupRefs.current[rowIndex] = element;
                                }
                                : undefined, direction: "row", spacing: 3, className: "scroll-group", "aria-hidden": copySetIndex > 0 || groupIndex > 0 || undefined, sx: groupSx, children: groupContent.map((item, itemIndex) => React.cloneElement(item, {
                                key: `${copySetIndex}-${groupIndex}-${itemIndex}`,
                                tabIndex: copySetIndex > 0 || groupIndex > 0
                                    ? -1
                                    : undefined,
                            })) }, groupIndex))) }, copySetIndex))) }) }, rowIndex));
        }) }, id));
};
const StyledBox = (props) => {
    const { children, isUniqueRow, reverse } = props;
    const createGradient = (direction) => {
        const gradientDirection = direction === "left" ? "to right" : "to left";
        const gradientColor = isUniqueRow
            ? "rgba(255,255,255,1)"
            : "rgba(0,0,255,1)";
        return {
            content: '""',
            position: "absolute",
            [direction]: 0,
            top: 0,
            bottom: 0,
            width: "40px",
            background: `linear-gradient(${gradientDirection}, ${gradientColor} 0%, rgba(255,255,255,0) 100%)`,
            zIndex: 1,
            pointerEvents: "none",
        };
    };
    return (_jsx(Box, { sx: {
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            position: "relative",
            ...(reverse && { transform: "scaleX(-1)" }),
            "&::before": { ...createGradient("left") },
            "&::after": { ...createGradient("right") },
        }, children: children }));
};
export default AutoScrollingInfos;
